import keyBy from "lodash-es/keyBy";

import { Space } from "types/generated";

/**
 * Get the full path of a space starting from the root space.
 */
const getParentsPath = (map: Record<string, Space>, space: Space): string => {
  let path = space.id;
  let currentSpace: Space | undefined = space;

  while (currentSpace?.parentSpace) {
    const parentSpace: Space | undefined = map[currentSpace.parentSpace];

    /* The backend should return every ancestor,
     but if a parent is missing from the loaded list,
     stop here with the partial path instead of crashing the whole app. */
    if (!parentSpace) {
      break;
    }

    path = `${parentSpace.id}/${path}`;
    currentSpace = parentSpace;
  }

  return path;
};

/**
 * Generate a map of space id to its full id path starting from the root space.
 *
 * Example:
 * - root: root
 * - legacy: root/legacy
 * - custom-space-id: root/legacy/custom-space-id
 * - another-space-id: root/another-space-id
 */
export const generateSpaceHierarchyMap = (spaces: Space[] = []) => {
  const spacesById = keyBy(spaces, "id");

  return Object.fromEntries(spaces.map((space) => [space.id, getParentsPath(spacesById, space)]));
};

// TODO: Remove this once backend returns space-hierarchy
export const getFullSpaceNamePath = (
  spaceId: string,
  spaceHierarchy: Record<string, string>,
  spaces: Space[]
) => {
  const spacesById = keyBy(spaces, "id");

  const idHierarchy = spaceHierarchy[spaceId];

  /* Fall back to the space's own name/id when its hierarchy path is missing or
   references spaces absent from the loaded list, rather than throwing. */
  if (!idHierarchy) {
    return spacesById[spaceId]?.name ?? spaceId;
  }

  return idHierarchy
    .split("/")
    .map((id) => spacesById[id]?.name ?? id)
    .join("/");
};

export type SpacePathSegment = { id: string; name: string };

/**
 * Resolve the path of a space, from the root, into ordered `{ id, name }` segments.
 *
 * Ancestors the caller can't see (restricted access) are omitted, so this can return a
 * partial path — or, with no hierarchy at all, just the space itself.
 */
// TODO: Remove this once backend returns space-hierarchy
export const getSpacePathSegments = (
  spaceId: string,
  spaceHierarchy: Record<string, string> = {},
  spaces: Space[] = []
): SpacePathSegment[] => {
  const spacesById = keyBy(spaces, "id");
  const idHierarchy = spaceHierarchy[spaceId];

  if (!idHierarchy) {
    const space = spacesById[spaceId];

    return space ? [{ id: space.id, name: space.name }] : [];
  }

  return idHierarchy.split("/").reduce<SpacePathSegment[]>((segments, id) => {
    const space = spacesById[id];

    if (space) {
      segments.push({ id: space.id, name: space.name });
    }

    return segments;
  }, []);
};

export const isLegacySpace = (id: string) => id === "legacy";

export const isRootSpace = (id: string) => id === "root";
